import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, CheckCircle2, Activity, Volume2, VolumeX, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import Logo from "@/components/Logo";
import GirdDigitalGoniometer from "@/components/GirdDigitalGoniometer";
import FmsGuidedTest from "@/components/FmsGuidedTest";
import MovementFindingsCard from "@/components/MovementFindingsCard";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * OnboardingScreening
 * ───────────────────
 * The gated screening wizard new ArmIQ athletes complete before unlocking the
 * dashboard. Three sequential blocks:
 *
 *   1. MODE          — solo self / coach-administered / parent-administered
 *   2. GIRD          — digital-goniometer on both arms → POST /screening/gird
 *   3. FMS           — 7-screen guided wizard → POST /screening/fms
 *
 * Voice coaching is on by default for the SELF mode (athlete is supine and
 * can't look at the screen). Coach/parent mode defaults TTS off because the
 * administrator is reading the screen.
 *
 * Query params:
 *   ?playerId=<user_id>  — coach/parent administering for a specific roster
 *                          athlete (passed straight through to backend).
 *   ?mode=coach|self|parent — bypass the mode-selection step.
 */
const STEPS = ["mode", "dominant", "gird_dom", "gird_non_dom", "fms", "done"];

export default function OnboardingScreening() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const playerIdParam = params.get("playerId") || null;
  const modeParam = params.get("mode") || null;

  const [step, setStep] = useState(modeParam ? "dominant" : "mode");
  const [mode, setMode] = useState(modeParam || "self");
  const [dominant, setDominant] = useState("right");
  const [girdResult, setGirdResult] = useState({});  // { ir_dominant, ir_non_dominant, method }
  const [girdSummary, setGirdSummary] = useState(null);
  const [fmsSummary, setFmsSummary] = useState(null);
  const [ttsOn, setTtsOn] = useState(true);
  const [screeningMode, setScreeningMode] = useState(null);   // 'armiq_builtin' | 'local_staff' | null

  // Fetch the team's screening assessment mode. If the HC selected
  // 'local_staff', the self-test wizards are replaced with a hand-off page.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/teams/me/screening-mode");
        if (!cancelled) setScreeningMode(data.mode || "armiq_builtin");
      } catch {
        if (!cancelled) setScreeningMode("armiq_builtin");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-disable voice when a coach/parent administers (they read the screen)
  useEffect(() => {
    if (mode !== "self") setTtsOn(false);
  }, [mode]);

  const speak = async (text) => {
    if (!ttsOn) return;
    try {
      const { data } = await api.post("/tts/speak", { text });
      if (data?.audio_b64) new Audio(`data:audio/mp3;base64,${data.audio_b64}`).play().catch(() => {});
    } catch { /* TTS is best-effort */ }
  };

  // Voice cue for each phase
  useEffect(() => {
    if (step === "dominant") speak("Tell us which arm is your throwing arm. This becomes your dominant arm for measurement.");
    if (step === "gird_dom") speak("First, your dominant arm. Lie on your back. Bring your shoulder out to ninety degrees and bend your elbow to ninety degrees.");
    if (step === "gird_non_dom") speak("Now your non-dominant arm. Same position. Lie on your back, shoulder at ninety, elbow at ninety.");
    if (step === "fms") speak("Functional movement screen. Seven movements. Follow the cues on screen.");
  }, [step, speak]);

  // ── GIRD: dominant arm submit handler ──
  const onDomDone = (angle, method) => {
    setGirdResult({ ...girdResult, ir_dominant: angle, method });
    setStep("gird_non_dom");
  };
  // ── GIRD: non-dominant arm submit handler — POST to backend ──
  const onNonDomDone = async (angle, method) => {
    const payload = {
      ir_dominant: girdResult.ir_dominant,
      ir_non_dominant: angle,
      dominant_arm: dominant,
      method: mode === "coach" ? "coach_gyro" : method,
      proxy_role: mode,
    };
    if (playerIdParam) payload.player_user_id = playerIdParam;
    try {
      const { data } = await api.post("/screening/gird", payload);
      setGirdSummary(data);
      const sev = data.severity;
      if (sev === "risk" || sev === "concern") {
        toast.error(`GIRD = ${data.gird_deg}° (${sev}). Corrective stretches auto-prescribed.`);
      } else {
        toast.success(`GIRD = ${data.gird_deg}° (${sev})`);
      }
      setStep("fms");
    } catch (e) {
      toast.error("Could not save GIRD result. Please try again.");
    }
  };

  const onFmsDone = (data) => { setFmsSummary(data); setStep("done"); };

  // ── HEADER ──
  const header = (
    <div className="max-w-2xl mx-auto px-6 pt-10 pb-6 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <Logo size="sm" variant="lockup" linkTo="/" dark />
        <span className="text-[10px] tracking-[0.25em] uppercase text-white/40 font-bold">MotionIQ + RotationIQ Screen</span>
      </div>
      <button onClick={() => setTtsOn((v) => !v)} className="text-white/50 hover:text-white text-xs flex items-center gap-1.5"
              data-testid="screening-toggle-voice">
        {ttsOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />} Voice {ttsOn ? "On" : "Off"}
      </button>
    </div>
  );

  // ── LOCAL STAFF MODE — bypass self-tests, show hand-off page ──
  if (screeningMode === "local_staff" && step !== "done") {
    return (
      <div className="min-h-screen" style={{ backgroundColor: "var(--ink-1)" }} data-testid="screening-local-staff-bypass">
        {header}
        <div className="max-w-2xl mx-auto px-6 pb-16">
          <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-6">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: "var(--brand-secondary)" }}>
              <ShieldCheck className="h-3.5 w-3.5" /> Local AT / PT mode
            </div>
            <h1 className="font-display font-black text-2xl md:text-3xl text-white mt-3 tracking-tighter">
              Your screening is handled by your team's staff.
            </h1>
            <p className="text-sm text-white/65 mt-3 leading-relaxed">
              Your Head Coach has configured ArmIQ so that FMS &amp; GIRD baseline testing is performed by your team's local Athletic Training or Physical Therapy staff. No self-test required.
            </p>
            <p className="text-sm text-white/65 mt-3 leading-relaxed">
              Once your staff completes the assessment, they will enter the values into your profile and your dashboard will unlock automatically. You'll receive a notification when that happens.
            </p>
            <div className="mt-5 rounded-lg border border-white/10 bg-black/30 p-4">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/45">What to do now</div>
              <ul className="text-sm text-white/80 mt-2 space-y-1.5 list-disc list-inside">
                <li>Schedule your screening with your team's AT or PT staff.</li>
                <li>Complete your first <b className="text-white">daily readiness log</b> in the meantime — it doesn't require baseline data.</li>
                <li>You'll get pinged the moment your baseline lands.</li>
              </ul>
            </div>
            <div className="mt-6 flex gap-3">
              <Button onClick={() => nav("/portal")} className="flex-1 text-white" style={{ backgroundColor: "var(--brand-secondary)" }}
                      data-testid="screening-local-staff-portal">
                Go to my portal
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // ── DONE PAGE ──
  if (step === "done") {
    const girdSev = girdSummary?.severity || "ok";
    const fmsTotal = fmsSummary?.fms_total || 0;
    return (
      <div className="min-h-screen" style={{ backgroundColor: "var(--ink-1)" }} data-testid="screening-done">
        {header}
        <div className="max-w-2xl mx-auto px-6 pb-16">
          <div className="text-center mb-6">
            <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-400" />
            <h1 className="font-display font-black text-3xl text-white mt-4">Baseline Captured</h1>
            <p className="text-sm text-white/60 mt-2">Your dashboard is unlocked. Re-screen in 90 days.</p>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="rounded-xl p-4 bg-black/30 border border-white/10">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--brand-secondary)]">GIRD</div>
              <div className="font-display font-black text-2xl text-white mt-1">{girdSummary?.gird_deg ?? "—"}°</div>
              <div className={`text-xs mt-0.5 font-bold uppercase tracking-wider ${
                girdSev === "risk" ? "text-red-400" : girdSev === "concern" ? "text-amber-400" : girdSev === "caution" ? "text-yellow-300" : "text-emerald-400"
              }`}>{girdSev}</div>
            </div>
            <div className="rounded-xl p-4 bg-black/30 border border-white/10">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--brand-secondary)]">FMS Total</div>
              <div className="font-display font-black text-2xl text-white mt-1">{fmsTotal} / 21</div>
              <div className={`text-xs mt-0.5 font-bold uppercase tracking-wider ${fmsTotal >= 14 ? "text-emerald-400" : "text-amber-400"}`}>
                {fmsTotal >= 14 ? "Healthy band" : "Risk band"}
              </div>
            </div>
          </div>
          {(girdSev === "risk" || girdSev === "concern") && (
            <div className="rounded-lg p-4 border border-amber-500/30 bg-amber-500/5 text-sm text-amber-200 mb-6">
              Corrective stretches (sleeper, cross-body, doorway PNF) have been auto-prescribed. Open Routine to view.
            </div>
          )}
          <div className="flex gap-3">
            <Button onClick={() => nav(user?.role === "coach" || user?.role === "admin" ? "/dashboard" : "/portal")}
                    className="flex-1" style={{ backgroundColor: "var(--brand-secondary)" }}
                    data-testid="screening-go-dashboard">
              Open my dashboard
            </Button>
          </div>

          {/* Intelligence Engine output — the Why-pane the blueprint promised */}
          <div className="mt-8">
            <MovementFindingsCard role={user?.role === "coach" || user?.role === "admin" ? "coach" : "player"} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--ink-1)" }} data-testid="onboarding-screening">
      {header}
      <div className="max-w-2xl mx-auto px-6 pb-16">
        {/* Stepper */}
        <div className="flex items-center gap-2 mb-6">
          {STEPS.filter((s) => s !== "done").map((s, i) => {
            const active = STEPS.indexOf(step) >= i;
            return (
              <div key={s} className={`h-1 flex-1 rounded ${active ? "bg-[var(--brand-secondary)]" : "bg-white/10"}`} />
            );
          })}
        </div>

        {/* MODE SELECTION */}
        {step === "mode" && (
          <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-6" data-testid="screening-mode">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)] mb-2">Who&apos;s running this?</div>
            <h2 className="font-display font-black text-2xl text-white mb-1">Pick a testing mode</h2>
            <p className="text-sm text-white/60 mb-5">
              GIRD and FMS measurements need accurate scapular stabilization. We strongly recommend a coach or parent administers — but a solo athlete can self-test with a strap and voice cues.
            </p>
            <div className="space-y-2">
              {[
                { v: "self",   t: "I'm self-testing", d: "Solo athlete — voice cues on, strap the phone to your forearm." },
                { v: "parent", t: "A parent is testing me", d: "Parent runs the test, you stay supine — best for youth players." },
                { v: "coach",  t: "My coach is testing me", d: "Team / clinical setting — coach administers and grades." },
              ].map((opt) => (
                <button key={opt.v} onClick={() => setMode(opt.v)} data-testid={`screening-mode-${opt.v}`}
                  className={`w-full text-left rounded-lg border px-4 py-3 transition-all
                              ${mode === opt.v ? "border-[var(--brand-secondary)] bg-[var(--brand-secondary)]/10" : "border-white/10 bg-black/30 hover:border-white/30"}`}>
                  <div className="text-white font-bold">{opt.t}</div>
                  <div className="text-xs text-white/55 mt-0.5">{opt.d}</div>
                </button>
              ))}
            </div>
            <Button onClick={() => setStep("dominant")} className="w-full mt-6"
                    style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="screening-mode-continue">
              Continue
            </Button>
          </Card>
        )}

        {/* DOMINANT ARM */}
        {step === "dominant" && (
          <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-6" data-testid="screening-dominant">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)] mb-2">Step 1 of 4 — Throwing arm</div>
            <h2 className="font-display font-black text-2xl text-white mb-1">Which arm do you throw with?</h2>
            <p className="text-sm text-white/60 mb-5">Internal-rotation deficit on the throwing arm is what we&apos;re testing for.</p>
            <div className="grid grid-cols-2 gap-3">
              {["right", "left"].map((arm) => (
                <button key={arm} onClick={() => setDominant(arm)} data-testid={`screening-arm-${arm}`}
                  className={`rounded-lg border px-4 py-6 transition-all
                              ${dominant === arm ? "border-[var(--brand-secondary)] bg-[var(--brand-secondary)]/10" : "border-white/10 bg-black/30 hover:border-white/30"}`}>
                  <div className="text-white font-bold text-lg capitalize">{arm}</div>
                  <div className="text-[10px] text-white/50 mt-1 uppercase tracking-wide">Throwing arm</div>
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={() => setStep("mode")} variant="outline" className="border-white/20 text-white" data-testid="screening-back">
                <ChevronLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button onClick={() => setStep("gird_dom")} className="flex-1"
                      style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="screening-dominant-continue">
                Start GIRD measurement
              </Button>
            </div>
          </Card>
        )}

        {/* GIRD — DOMINANT */}
        {step === "gird_dom" && (
          <>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)] mb-2">Step 2 of 4 · GIRD · Dominant ({dominant})</div>
            <h2 className="font-display font-black text-2xl text-white mb-4">Measure your dominant arm</h2>
            <GirdDigitalGoniometer key={`gird-dom-${dominant}`} label={`Dominant Arm (${dominant})`} measureType="IR"
                                   onDone={onDomDone} onCancel={() => setStep("dominant")} />
          </>
        )}

        {/* GIRD — NON-DOMINANT */}
        {step === "gird_non_dom" && (
          <>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)] mb-2">Step 2 of 4 · GIRD · Non-Dominant</div>
            <h2 className="font-display font-black text-2xl text-white mb-4">Now your non-dominant arm</h2>
            <div className="rounded-lg p-3 mb-3 border border-emerald-500/30 bg-emerald-500/5 text-xs text-emerald-200">
              <span className="font-bold">Dominant ({dominant}):</span> {girdResult.ir_dominant}° captured.
            </div>
            <GirdDigitalGoniometer key="gird-non-dom" label="Non-Dominant Arm" measureType="IR"
                                   onDone={onNonDomDone} onCancel={() => setStep("gird_dom")} />
          </>
        )}

        {/* FMS */}
        {step === "fms" && (
          <>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)] mb-2">Step 3 of 4 · Functional Movement Screen</div>
            <h2 className="font-display font-black text-2xl text-white mb-4">Seven movements, three minutes</h2>
            <FmsGuidedTest mode={mode} playerUserId={playerIdParam} ttsEnabled={ttsOn} onComplete={onFmsDone}
                           onCancel={() => setStep("gird_non_dom")} />
          </>
        )}

        {/* PRIVACY / CONSENT NOTE */}
        <div className="mt-8 text-[11px] text-white/35 flex items-start gap-2">
          <ShieldCheck className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <p>Results are stored to your ArmIQ profile and shared with your coach (if you&apos;re on a team).
            Re-screen is suggested every 90 days. <Link to="/privacy" className="underline">Privacy policy</Link>.</p>
        </div>

        <div className="mt-6">
          <Link to={user?.role === "coach" || user?.role === "admin" ? "/dashboard" : "/portal"}
                className="text-xs text-white/40 hover:text-white inline-flex items-center gap-1" data-testid="screening-skip">
            <Activity className="h-3.5 w-3.5" /> Skip and finish later
          </Link>
        </div>
      </div>
    </div>
  );
}
